import { Order, NextPaymentResponse, StatusResponse } from '../models/APIModels';

export type ProgressStepState = 'completed' | 'current' | 'future' | 'failed';

export interface ProgressStep {
  id: string;
  icon: string;
  label: string;
  sublabel?: string;
  timestamp?: number;
  state: ProgressStepState;
}

export interface CancelledInfo {
  cancelledAt?: number;
}

export function isOrderCancelled(order: Order): boolean {
  if (order.deletedAt && order.deletedAt > 0) return true;
  if (order.orderType === 'removal' && order.status === 'cancelled') return true;
  return false;
}

export function getCancelledInfo(order: Order): CancelledInfo {
  return { cancelledAt: order.deletedAt && order.deletedAt > 0 ? order.deletedAt : undefined };
}

function buildStatusMap(statuses: StatusResponse[] | undefined): Map<number, number> {
  const map = new Map<number, number>();
  (statuses || []).forEach(s => {
    const existing = map.get(s.statusId);
    if (existing === undefined || s.updatedAt > existing) {
      map.set(s.statusId, s.updatedAt);
    }
  });
  return map;
}

function markCurrent(steps: ProgressStep[]): ProgressStep[] {
  const firstFutureIndex = steps.findIndex(s => s.state === 'future');
  if (firstFutureIndex > 0 && steps[firstFutureIndex - 1].state === 'completed') {
    steps[firstFutureIndex] = { ...steps[firstFutureIndex], state: 'current' };
  } else if (firstFutureIndex === 0) {
    steps[0] = { ...steps[0], state: 'current' };
  }
  return steps;
}

function buildStorageSteps(order: Order, nextPayment?: NextPaymentResponse): ProgressStep[] {
  const statusMap = buildStatusMap(order.statuses);
  const hasStatus = (id: number) => statusMap.has(id);
  const ts = (id: number) => statusMap.get(id);

  const subscriptionPath = !!order.stripeSubscriptionId;
  const paidOrActive = hasStatus(2) || hasStatus(7);
  const finalBillPaid = hasStatus(8);
  const delivered = hasStatus(6) || finalBillPaid;

  const steps: ProgressStep[] = [
    {
      id: 'placed',
      icon: '✅',
      label: 'Order Placed',
      sublabel: 'Finalising your booking',
      timestamp: ts(1),
      state: hasStatus(1) ? 'completed' : 'future',
    },
    {
      id: 'payment',
      icon: '💳',
      label: subscriptionPath ? 'Monthly Payments Active' : 'Payment',
      sublabel: subscriptionPath
        ? 'Awaiting first subscription charge'
        : 'Awaiting payment confirmation',
      timestamp: ts(7) ?? ts(2),
      state: paidOrActive ? 'completed' : 'future',
    },
    {
      id: 'collection',
      icon: '📮',
      label: 'Collection',
      sublabel: 'Our courier will be in touch to confirm a pickup time',
      timestamp: ts(3),
      state: hasStatus(3) ? 'completed' : 'future',
    },
    {
      id: 'stored',
      icon: '🏢',
      label: 'Safely in Storage',
      sublabel: 'Your items are on their way — we\'ll check them in when they arrive',
      timestamp: ts(4),
      state: hasStatus(4) ? 'completed' : 'future',
    },
    {
      id: 'return',
      icon: '📦',
      label: 'Return Requested',
      sublabel: 'Use the "Request Return" link above when you\'re ready to get your items back',
      timestamp: ts(5),
      state: hasStatus(5) ? 'completed' : 'future',
    },
    {
      id: 'outForDelivery',
      icon: '🚚',
      label: 'Out for Delivery',
      sublabel: 'Heading to your delivery address',
      state: delivered ? 'completed' : 'future',
    },
    {
      id: 'delivered',
      icon: '✨',
      label: 'Delivered',
      sublabel: finalBillPaid ? 'Final bill paid' : 'Your items have been returned',
      timestamp: ts(8) ?? ts(6),
      state: delivered ? 'completed' : 'future',
    },
  ];

  markCurrent(steps);

  if (paidOrActive) {
    if (nextPayment?.status === 'past_due') {
      const idx = steps.findIndex(s => s.id === 'payment');
      if (idx >= 0) {
        steps[idx] = {
          ...steps[idx],
          state: 'failed',
          sublabel: 'Payment failed — please update your payment method',
        };
      }
    } else if (nextPayment?.status === 'trialing') {
      const idx = steps.findIndex(s => s.id === 'payment');
      if (idx >= 0) {
        steps[idx] = {
          ...steps[idx],
          sublabel: 'Trial period active',
        };
      }
    }
  }

  return steps;
}

function buildShippingSteps(order: Order): ProgressStep[] {
  const statusMap = buildStatusMap(order.statuses);
  const hasStatus = (id: number) => statusMap.has(id);
  const ts = (id: number) => statusMap.get(id);

  const delivered = hasStatus(6) || hasStatus(8);
  const inTransit = hasStatus(3) && !delivered;
  const received = hasStatus(4) && !delivered;

  const steps: ProgressStep[] = [
    {
      id: 'placed',
      icon: '✅',
      label: 'Order Placed',
      sublabel: 'Finalising your booking',
      timestamp: ts(1),
      state: hasStatus(1) ? 'completed' : 'future',
    },
    {
      id: 'paid',
      icon: '💳',
      label: 'Paid',
      sublabel: 'Awaiting payment confirmation',
      timestamp: ts(2),
      state: hasStatus(2) ? 'completed' : 'future',
    },
    {
      id: 'shipped',
      icon: '📮',
      label: 'In Transit',
      sublabel: received
        ? 'Received at our hub — preparing for onward delivery'
        : 'We\'ll send tracking details once your items are on the move',
      timestamp: ts(4) ?? ts(3),
      state: hasStatus(3) || hasStatus(4) ? 'completed' : 'future',
    },
    {
      id: 'delivered',
      icon: '✨',
      label: 'Delivered',
      sublabel: inTransit
        ? 'On its way — should arrive soon'
        : 'Your items have arrived safely',
      timestamp: ts(6) ?? ts(8),
      state: delivered ? 'completed' : 'future',
    },
  ];

  return markCurrent(steps);
}

function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function buildRemovalSteps(order: Order): ProgressStep[] {
  const now = Date.now();
  const moveDate = order.moveDate;
  const confirmed = order.status === 'confirmed' || order.status === 'completed';
  const completed = order.status === 'completed';
  const paid = !!order.stripePurchaseId;
  const moveDatePast = typeof moveDate === 'number' && moveDate > 0 && moveDate < now;
  const moveDateToday = typeof moveDate === 'number' && moveDate > 0 && isSameDay(moveDate, now);

  const steps: ProgressStep[] = [
    {
      id: 'quote',
      icon: '✅',
      label: 'Quote Confirmed',
      sublabel: 'Awaiting confirmation from our team',
      timestamp: confirmed ? order.updatedAt : undefined,
      state: confirmed ? 'completed' : 'future',
    },
    {
      id: 'payment',
      icon: '💳',
      label: 'Payment Received',
      sublabel: 'Waiting for your payment to clear',
      state: paid ? 'completed' : 'future',
    },
    {
      id: 'scheduled',
      icon: '📅',
      label: 'Move Scheduled',
      sublabel: moveDate ? undefined : 'Awaiting a move date',
      timestamp: moveDate,
      state: moveDate ? 'completed' : 'future',
    },
    {
      id: 'movingDay',
      icon: '🚚',
      label: 'Moving Day',
      sublabel: moveDateToday
        ? 'Our team is on the way'
        : 'Our team will arrive on your scheduled date',
      state: moveDatePast || completed ? 'completed' : 'future',
    },
    {
      id: 'complete',
      icon: '🏡',
      label: 'Move Complete',
      sublabel: 'Final drop-off at your new address',
      state: completed ? 'completed' : 'future',
    },
  ];

  markCurrent(steps);

  if (order.storageId) {
    steps.push({
      id: 'storageActive',
      icon: '📦',
      label: 'Storage Active',
      sublabel:
        typeof order.monthlyStorageCost === 'number' && order.monthlyStorageCost > 0
          ? `Monthly storage running in the background`
          : 'Storage add-on active',
      state: 'completed',
    });
  }

  return steps;
}

export function getOrderProgressSteps(
  order: Order,
  nextPayment?: NextPaymentResponse
): ProgressStep[] | null {
  if (isOrderCancelled(order)) return null;

  if (order.orderType === 'removal') {
    return buildRemovalSteps(order);
  }

  if (order.storageRequired) {
    return buildStorageSteps(order, nextPayment);
  }

  return buildShippingSteps(order);
}
